import { useMutation, useQuery } from '@apollo/client'
import { ModalForm, TextInput, useForm } from '@faceup/form'
import { useMessage } from '@faceup/ui-base'
import { yup } from '@faceup/validation'
import { useEffect } from 'react'
import { graphql } from '../__generated__'

type SetupPaylocityModalProps = {
  institutionId: string
  open: boolean
  onClose: () => void
}

const query = {
  SetupPaylocityModalQuery: graphql(`
    query SetupPaylocityModalQuery($institutionId: UUID!) {
      institution(organizationalUnitId: $institutionId) {
        id
        paylocityCompanyId
      }
    }
  `),
}

const mutation = {
  SetupPaylocity: graphql(`
    mutation setupPaylocity($input: SetupPaylocityInput!) {
      setupPaylocity(input: $input) {
        success
      }
    }
  `),
}

const schema = yup.object({
  paylocityCompanyId: yup.string().required(),
})

export const SetupPaylocityModal = ({ open, onClose, institutionId }: SetupPaylocityModalProps) => {
  const { data, loading } = useQuery(query.SetupPaylocityModalQuery, {
    variables: { institutionId },
  })

  const form = useForm({
    schema,
    afterSubmit: 'persistValues',
    defaultValues: {
      paylocityCompanyId: '',
    },
  })

  useEffect(() => {
    form.reset({
      paylocityCompanyId: data?.institution?.paylocityCompanyId ?? '',
    })
  }, [data?.institution?.paylocityCompanyId, form.reset])

  const message = useMessage()

  const [setupPaylocity, { loading: setupPaylocityLoading }] = useMutation(
    mutation.SetupPaylocity,
    {
      refetchQueries: ['InstitutionQuery'],
      onCompleted: () => {
        message.success('Paylocity Company ID set')
        onClose()
      },
      onError: () => {
        message.error('Paylocity setup failed')
      },
    }
  )

  return (
    <ModalForm
      form={form}
      submitButtonText='save'
      title='Paylocity Setup'
      loading={loading || setupPaylocityLoading}
      open={open}
      onSubmit={async values => {
        const result = await setupPaylocity({
          variables: {
            input: {
              motherId: institutionId,
              paylocityCompanyId: values.paylocityCompanyId,
            },
          },
        })

        if (result.errors) {
          return false
        }
        return true
      }}
      onClose={onClose}
    >
      <TextInput control={form.control} name='paylocityCompanyId' label='Company ID' />
    </ModalForm>
  )
}
